/**
 * LRTransformer
 * @class LRTransformer
 * @constructor
 */
export class LRTransformer {
    /**
     * @method constructor
     * Linear Referencing Functions
     * @param {Object} axis The featureCollection (fc) representing the axis geometry.
     * @param {number[][]} k The calibration factors.
     */
    constructor(axis, k, vtolerance = 100) {
        this._vtolerance = 100;
        this.axis = axis;
        this.k = k;
        this._vtolerance = vtolerance;
    }
    /**
     * Calculates uvw-Coordinates from xyz-Coordinates
     * @method xyz2uvw
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @return {?Object}
     */
    xyz2uvw(x, y, z) {
        //Check if an axis is available
        if (this.axis != null) {
            let uvwcoords = null;
            //Iterate through all Features that build an axis
            for (let i = 0; i < this.axis["features"].length; i++) {
                const theFeature = this.axis["features"][i];
                //Check if the FeatureType is LineString
                if (theFeature.geometry.type == "LineString") {
                    const coords = theFeature.geometry.coordinates;
                    const normal = this._searchNormal(coords, x, y, z);
                    if (normal != null) {
                        normal.featureidx = i;
                        normal.lrcoords[0] = this._calibrate(normal.featureidx, normal.lrcoords[0]);
                        if (uvwcoords === null) {
                            uvwcoords = normal;
                        }
                        else if (Math.abs(uvwcoords.lrcoords[1]) > Math.abs(normal.lrcoords[1])) {
                            uvwcoords = normal;
                        }
                    }
                }
            }
            if (uvwcoords != null)
                return uvwcoords;
            return null;
        }
        return null;
    }
    /**
     * Searches for a Normal on a LineString
     * @method _searchNormal
     * @param {number[][]} coordlist
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @return {?Object}
     * @private
     */
    _searchNormal(coordlist, x, y, z) {
        let u = 0;
        let v = 0;
        let w = 0;
        let ax = 0;
        let ay = 0;
        let az = 0;
        const normals = [];
        for (let j = 0; j < coordlist.length - 1; j++) {
            const xs1 = coordlist[j][0];
            const ys1 = coordlist[j][1];
            const zs1 = coordlist[j][2];
            const xs = coordlist[j + 1][0] - xs1;
            const ys = coordlist[j + 1][1] - ys1;
            const zs = coordlist[j + 1][2] - zs1;
            let mi = (xs * (x - xs1) + ys * (y - ys1)) / (Math.pow(xs, 2) + Math.pow(ys, 2));
            mi = Math.round(mi * 1000) / 1000;
            if (mi >= 0 && mi <= 1) {
                ax = xs1 + mi * xs;
                ay = ys1 + mi * ys;
                az = zs1 + mi * zs;
                //Berechnung von u
                u = u + Math.sqrt(Math.pow(ax - xs1, 2) + Math.pow(ay - ys1, 2));
                //Berechnung von v
                v = Math.sqrt(Math.pow(ax - x, 2) + Math.pow(ay - y, 2));
                const crossProd3 = xs * (y - ys1) - ys * (x - xs1);
                if (crossProd3 >= 0)
                    v = v * -1;
                //Berechnung von w
                w = z - az;
                if (Math.abs(v) < this._vtolerance /* && Math.abs(w) < 50*/) {
                    const normal = {
                        featureidx: null, //will be set later
                        vertexidx: j,
                        lrcoords: [u, v, w],
                        axpoint: [ax, ay, az],
                    };
                    normals.push(normal);
                }
                u = u + Math.sqrt(Math.pow(ax - (xs1 + xs), 2) + Math.pow(ay - (ys1 + ys), 2));
            }
            else if (mi > 1 && coordlist[j + 2]) {
                const xs3 = coordlist[j + 1][0];
                const ys3 = coordlist[j + 1][1];
                const zs3 = coordlist[j + 1][2];
                const xs2 = coordlist[j + 2][0] - xs3;
                const ys2 = coordlist[j + 2][1] - ys3;
                let mi2 = (xs2 * (x - xs3) + ys2 * (y - ys3)) / (Math.pow(xs2, 2) + Math.pow(ys2, 2));
                mi2 = Math.round(mi2 * 1000) / 1000;
                if (mi2 < 0) {
                    ax = xs3;
                    ay = ys3;
                    az = zs3;
                    //Berechnung von u
                    u = u + Math.sqrt(Math.pow(ax - xs1, 2) + Math.pow(ay - ys1, 2));
                    //Berechnung von v
                    v = Math.sqrt(Math.pow(ax - x, 2) + Math.pow(ay - y, 2));
                    const crossProd3 = xs2 * (y - ys3) - ys2 * (x - xs3);
                    if (crossProd3 >= 0)
                        v = v * -1;
                    //Berechnung von w
                    w = z - az;
                    if (Math.abs(v) < 100.0 /* && Math.abs(w) < 50*/) {
                        const normal = {
                            featureidx: null, //will be set later
                            vertexidx: j + 1,
                            lrcoords: [u, v, w],
                            axpoint: [ax, ay, az],
                        };
                        normals.push(normal);
                    }
                }
                else {
                    u = u + Math.sqrt(Math.pow(xs, 2) + Math.pow(ys, 2));
                }
            }
            else if (mi < 0) {
                u = u + Math.sqrt(Math.pow(xs, 2) + Math.pow(ys, 2));
            }
        }
        let retnormal = null;
        if (normals.length == 1) {
            retnormal = normals[0];
        }
        else if (normals.length > 1) {
            let cur_sqr_disttoaxis = 1e10;
            for (const normal of normals) {
                let sqr_disttoaxis = null;
                if (coordlist[0][2]) {
                    sqr_disttoaxis = Math.pow(normal.lrcoords[1], 2) + Math.pow(normal.lrcoords[2], 2);
                }
                else {
                    sqr_disttoaxis = Math.pow(normal.lrcoords[1], 2);
                }
                if (sqr_disttoaxis < cur_sqr_disttoaxis) {
                    retnormal = normal;
                    cur_sqr_disttoaxis = sqr_disttoaxis;
                }
            }
        }
        return retnormal;
    }
    /**
     * Calibrates the u-Value due to a nominalLength given on "addAxis"-method
     * @method _calibrate
     * @return {Object}
     * @private
     * @param featureidx
     * @param u
     */
    _calibrate(featureidx, u) {
        if (!this.k[featureidx]) {
            return u;
        }
        const k = this.k[featureidx][0];
        const offset = this.k[featureidx][1];
        return u / k + offset;
    }
    /**
     * Calibrates the u-Value due to a nominalLength given on "addAxis"-method
     * @method _calibrateToGeometry
     * @param {number} featureidx
     * @param {number} u
     * @return {number}
     * @private
     */
    _calibrateToGeometry(featureidx, u) {
        const k = this.k[featureidx][0];
        const offset = this.k[featureidx][1];
        return (u - offset) * k;
    }
    /**
     * Calculates the absolute u Value of all features in this.axis.
     * @method u2absU
     * @param {number} featureidx
     * @param {number} u
     * @return {number}
     */
    u2absU(featureidx, u) {
        let totalDistance = 0;
        for (let i = 0; i < featureidx; i++) {
            const coords = this.axis["features"][i].geometry.coordinates;
            let actualDistance = 0;
            for (let j = 0; j < coords.length - 1; j++) {
                //2D-Distance
                const dist = Math.sqrt(Math.pow(coords[j + 1][0] - coords[j][0], 2) + Math.pow(coords[j + 1][1] - coords[j][1], 2));
                actualDistance += dist;
            }
            totalDistance += actualDistance;
        }
        const u_g = this._calibrateToGeometry(featureidx, u);
        return totalDistance + u_g;
    }
    /**
     * Calculates the u Value from the absolute u Value. This will consider all features in this.axis.
     * @method absU2u
     * @param {number} absU
     * @return {number[]}
     */
    absU2u(absU) {
        const distarr = [0];
        for (let i = 0; i < this.axis["features"].length; i++) {
            let dist = 0;
            const coords = this.axis["features"][i].geometry.coordinates;
            for (let j = 0; j < coords.length - 1; j++) {
                dist += Math.sqrt(Math.pow(coords[j + 1][0] - coords[j][0], 2) + Math.pow(coords[j + 1][1] - coords[j][1], 2));
            }
            distarr.push(dist);
        }
        let min = 0;
        let max = distarr[0];
        let featureidx = null;
        if (absU < 0) {
            featureidx = 0;
        }
        else if (absU > distarr.reduce((a, b) => a + b)) {
            featureidx = distarr.length - 1;
        }
        else {
            for (let i = 0; i < distarr.length - 1; i++) {
                min += distarr[i];
                max += distarr[i + 1];
                if (absU > min && absU < max) {
                    featureidx = i;
                    break;
                }
            }
        }
        const u_g = absU - min;
        const u = this._calibrate(featureidx, u_g);
        return [u, featureidx];
    }
    /**
     * Calculates xyz-Coordinates from uvw-Coordinates
     * @method uvw2xyz
     * @param {number} featureidx
     * @param {number} u
     * @param {number} v
     * @param {number} w
     * @return {Object}
     */
    uvw2xyz(featureidx, u, v, w) {
        // TODO: Add support for using the navigation axis (like in the function xyz2uvw)
        let xyz = null;
        if (this.axis != null) {
            if (featureidx < this.axis["features"].length) {
                if (!this.axis["features"][featureidx]) {
                    return xyz;
                }
                const coords = this.axis["features"][featureidx].geometry.coordinates;
                let u_g = this._calibrateToGeometry(featureidx, u);
                let measure = 0;
                for (let i = 0; i < coords.length - 1; i++) {
                    const dist = Math.sqrt(Math.pow(coords[i + 1][0] - coords[i][0], 2) + Math.pow(coords[i + 1][1] - coords[i][1], 2));
                    measure = measure + dist;
                    if (i == coords.length - 2 && measure < u_g) {
                        u_g = measure;
                    }
                    if (measure >= u_g) {
                        const m = (u_g - (measure - dist)) / dist;
                        //a-Vektor (vom Stützpuntk bis zum Achspunkt)
                        let xa = m * (coords[i + 1][0] - coords[i][0]);
                        let ya = m * (coords[i + 1][1] - coords[i][1]);
                        let za = m * (coords[i + 1][2] - coords[i][2]);
                        //pa-Vektor (Ortsvektor des Achspunktes)
                        const xpa = coords[i][0] + xa;
                        const ypa = coords[i][1] + ya;
                        const zpa = coords[i][2] + za;
                        if (parseInt(m.toFixed(10)) == 0) {
                            xa = coords[i + 1][0] - coords[i][0];
                            ya = coords[i + 1][1] - coords[i][1];
                            za = coords[i + 1][2] - coords[i][2];
                        }
                        //b-Vektor (Rechtwinklig zum a-Vektor)
                        const xb = ya;
                        const yb = -xa;
                        //p-Vektor (b-Vektor auf länge v skaliert
                        const xp = (xb / Math.sqrt(Math.pow(xb, 2) + Math.pow(yb, 2))) * v;
                        const yp = (yb / Math.sqrt(Math.pow(xb, 2) + Math.pow(yb, 2))) * v;
                        //Ortsvektor des gesuchten Punktes
                        xyz = [xpa + xp, ypa + yp, zpa + w];
                        break;
                    }
                }
            }
        }
        return xyz;
    }
}
//# sourceMappingURL=lrtransformer.js.map